import { useState } from 'react';
import type { ActionCredential } from '@customerio/hydra-ui-api';
import { findPushPlatform, type PushProvider } from '@/features/push-credentials';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { useAnalytics } from '@/services/analytics';
import { ApnsProviderForm } from '../components/apns-provider-form';
import { FcmProviderForm } from '../components/fcm-provider-form';
import { IosProviderChooser } from '../components/provider-chooser';
import { PushPlatformEmptyState } from '../components/push-platform-empty-state';
import { PushSettingsLoading } from '../components/push-settings-loading';
import {
  usePushConfigurationViewedAnalytics,
  usePushCredentials,
  usePushPermissions,
  usePushPlatformBreadcrumb,
  usePushPlatforms,
  type PushCredentialsState,
  type PushPlatformsState,
} from '../hooks';
import type { PushSaveContext } from '../types';

function useIosPushAnalytics(provider: PushProvider) {
  const { track } = useAnalytics();
  const name = provider.toUpperCase();

  return {
    reportSaved: ({ wasConfigured, hadFcmCredential }: PushSaveContext) => {
      void track('push', wasConfigured ? 'authorizationUpdated' : 'authorizationAdded', {
        platform: 'iOS',
        provider: name,
      });
      // One Firebase key serves both platforms, so turning iOS on with a key
      // the workspace did not have turns Android on at the same time.
      if (provider === 'fcm' && !hadFcmCredential) {
        void track('push', 'authorizationAdded', { platform: 'Android', provider: 'FCM' });
      }
    },
    reportDisabled: () => {
      void track('push', 'pushDisabled', { platform: 'iOS', provider: name });
    },
  };
}

type IosProviderView = {
  credential: ActionCredential | null;
  showEmptyState: boolean;
  canSelectProvider: boolean;
  /**
   * Only an Apple key iOS is actually sending through can be removed from here;
   * a Firebase key is shared with Android and goes from its own tab.
   */
  showDisable: boolean;
};

function iosProviderView(
  configuredProvider: PushProvider | null,
  provider: PushProvider,
  isEditing: boolean,
  credentials: PushCredentialsState,
): IosProviderView {
  const isConfigured = configuredProvider != null;

  return {
    credential: provider === 'apns' ? credentials.apns : credentials.fcm,
    showEmptyState: !isConfigured && !isEditing,
    canSelectProvider: !isConfigured || isEditing,
    showDisable: provider === 'apns' && configuredProvider === 'apns',
  };
}

type IosPushConfigProps = {
  workspaceId: number;
  platformsState: PushPlatformsState;
  credentials: PushCredentialsState;
};

/**
 * Mounted only once the platforms and both credential slots resolve, so the
 * chooser opens on the service iOS actually sends through.
 */
function IosPushConfig({ workspaceId, platformsState, credentials }: IosPushConfigProps) {
  const { canCreate, canEdit, canDelete } = usePushPermissions();
  const { platforms, providers } = platformsState;

  const configuredProvider = findPushPlatform(platforms, 'ios')?.provider ?? null;
  const [isEditing, setIsEditing] = useState(false);
  const [provider, setProvider] = useState<PushProvider>(configuredProvider ?? 'apns');
  const { reportSaved, reportDisabled } = useIosPushAnalytics(provider);

  const view = iosProviderView(configuredProvider, provider, isEditing, credentials);

  usePushConfigurationViewedAnalytics('ios', provider, view.credential != null, !view.showEmptyState);

  if (view.showEmptyState) {
    return (
      <PushPlatformEmptyState platform="ios" canCreate={canCreate} onStartConfiguring={() => setIsEditing(true)} />
    );
  }

  const shared = {
    workspaceId,
    platform: 'ios',
    platforms,
    credential: view.credential,
    isEditing,
    isReading: platformsState.isFetching || credentials.isFetching,
    canEdit,
    canDelete,
    showDisable: view.showDisable,
    chooser: <IosProviderChooser provider={provider} canSelect={view.canSelectProvider} onSelect={setProvider} />,
    onEditingChange: setIsEditing,
    onCancel: () => {
      setIsEditing(false);
      setProvider(configuredProvider ?? 'apns');
    },
    onSaved: reportSaved,
    onDisabled: reportDisabled,
  } as const;

  if (provider === 'apns') {
    return <ApnsProviderForm {...shared} />;
  }

  return <FcmProviderForm {...shared} providers={providers} />;
}

type IosPushTabProps = {
  workspaceId: number;
};

function IosPushTab({ workspaceId }: IosPushTabProps) {
  const platformsState = usePushPlatforms(workspaceId);
  const credentials = usePushCredentials(workspaceId);

  if (platformsState.isPending || credentials.isPending) {
    return <PushSettingsLoading />;
  }

  return <IosPushConfig workspaceId={workspaceId} platformsState={platformsState} credentials={credentials} />;
}

export default function WorkspacePushIosSettingsPage() {
  const workspaceId = getWorkspaceIdFromCurrentUrl();
  usePushPlatformBreadcrumb(workspaceId, 'ios');

  if (workspaceId == null || workspaceId <= 0) {
    return <PushSettingsLoading />;
  }

  return <IosPushTab workspaceId={workspaceId} />;
}
